// frontend/src/pages/UserPage.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  User, Heart, Star, Calendar, Film,
  UserPlus, UserMinus, Loader, MessageSquare, BookOpen
} from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import MovieCard from '../components/MovieCard';
import { userAPI } from '../services/api';
import api from '../services/api';

// ─────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────
interface PublicUser {
  id: string;
  username: string;
  name: string;
  avatar?: string;
  bio?: string;
  createdAt: string;
  reviewsCount: number;
  favoritesCount: number;
  followersCount: number;
  followingCount: number;
}

interface MovieItem {
  id: number;
  title: string;
  overview?: string;
  posterUrl?: string;
  releaseYear?: number;
  voteAverage?: number;
  season?: string;
  holiday?: string;
  moods?: string[];
  tropes?: string[];
  favoritesCount?: number;
  reviewsCount?: number;
}

interface ReviewItem {
  id: string;
  content: string;
  rating: number;
  createdAt: string;
  likesCount: number;
  commentsCount: number;
  movie: {
    id: number;
    title: string;
    posterUrl?: string;
    releaseYear?: number;
  };
}

interface NotebookEntryItem {
  id: string;
  watchedOn?: string;
  loggedAt: string;
  note: string | null;
  movie: MovieItem;
}

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────
const UserPage: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const { state, openLoginModal } = useAppContext();

  const isOwnProfile = state.currentUser?.username === username;

  const [profileUser, setProfileUser] = useState<PublicUser | null>(null);
  const [favorites, setFavorites] = useState<MovieItem[]>([]);
  const [reviews, setReviews] = useState<ReviewItem[]>([]);
  const [watched, setWatched] = useState<MovieItem[]>([]);
  const [notebook, setNotebook] = useState<NotebookEntryItem[]>([]);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isFollowLoading, setIsFollowLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'favorites' | 'reviews' | 'memory' | 'notebook'>('reviews');
  const [error, setError] = useState('');

  // Set initial tab based on profile ownership
  useEffect(() => {
    setActiveTab(isOwnProfile ? 'favorites' : 'reviews');
  }, [isOwnProfile]);

  // ── Fetch profile data ──
  useEffect(() => {
    const fetchProfile = async () => {
      if (!username) return;
      try {
        setIsLoading(true);
        setError('');

        const [profileRes, reviewsRes, watchedRes, notebookRes] = await Promise.all([
          userAPI.getPublicProfile(username),
          userAPI.getPublicReviews(username),
          userAPI.getPublicWatched(username),
          userAPI.getPublicNotebook(username),
        ]);

        setProfileUser(profileRes.data.data);
        setReviews(reviewsRes.data.data);
        setWatched(watchedRes.data.data);
        setNotebook(notebookRes.data.data);

        // Vault (favorites) — only for own profile
        if (isOwnProfile) {
          const favRes = await userAPI.getPublicFavorites(username);
          setFavorites(favRes.data.data);
        }

        // Check follow status
        if (state.currentUser && profileRes.data.data) {
          try {
            const followRes = await api.get(`/users/${profileRes.data.data.id}/is-following`);
            setIsFollowing(followRes.data.data.isFollowing);
          } catch {
            // Not logged in or error, ignore
          }
        }
      } catch (err) {
        setError('User not found');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, [username, state.currentUser, isOwnProfile]);

  // ── Follow / unfollow ──
  const handleFollowToggle = async () => {
    if (!state.currentUser) {
      openLoginModal();
      return;
    }
    if (!profileUser) return;

    try {
      setIsFollowLoading(true);
      if (isFollowing) {
        await userAPI.unfollowUser(profileUser.id);
        setIsFollowing(false);
        setProfileUser(prev => prev ? { ...prev, followersCount: prev.followersCount - 1 } : null);
      } else {
        await userAPI.followUser(profileUser.id);
        setIsFollowing(true);
        setProfileUser(prev => prev ? { ...prev, followersCount: prev.followersCount + 1 } : null);
      }
    } catch (err) {
      console.error('Follow error:', err);
    } finally {
      setIsFollowLoading(false);
    }
  };

  // ── Loading ──
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-romcom-red animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading profile...</p>
        </div>
      </div>
    );
  }

  // ── Error ──
  if (error || !profileUser) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">User not found</h2>
          <p className="text-gray-500 mb-6">This profile doesn't exist or has been removed.</p>
          <button onClick={() => navigate('/')} className="btn-primary">Go Home</button>
        </div>
      </div>
    );
  }

  // ── Tabs config ──
  const tabs = [
    ...(isOwnProfile ? [{ key: 'favorites', label: 'The Vault', icon: Heart }] : []),
    { key: 'reviews', label: 'Reviews', icon: MessageSquare },
    { key: 'memory', label: 'Memory Lane', icon: Film },
    { key: 'notebook', label: 'Notebook', icon: BookOpen },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-romcom-pink/10 to-romcom-blue/10">
      <div className="container mx-auto px-4 pt-24 pb-16 max-w-5xl">

        {/* ── PROFILE HEADER ── */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-romcom-pink/20 to-romcom-blue/20 rounded-3xl p-8 mb-8"
        >
          <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
            {/* Avatar */}
            <div className="w-28 h-28 rounded-full overflow-hidden flex-shrink-0 shadow-lg">
              {profileUser.avatar ? (
                <img src={profileUser.avatar} alt={profileUser.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-romcom-pink to-romcom-purple flex items-center justify-center">
                  <User className="w-14 h-14 text-white" />
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 text-center md:text-left">
              <h1 className="text-2xl font-bold text-gray-800">{profileUser.name || profileUser.username}</h1>
              <p className="text-gray-500 mb-2">@{profileUser.username}</p>
              {profileUser.bio && (
                <p className="text-gray-600 mb-4 max-w-lg">{profileUser.bio}</p>
              )}

              {/* Follow stats */}
              <div className="flex items-center justify-center md:justify-start gap-6 text-sm">
                {isOwnProfile && (
                  <>
                    <div className="text-center">
                      <div className="font-bold text-gray-800 text-lg">{profileUser.followersCount}</div>
                      <div className="text-gray-500">Followers</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-gray-800 text-lg">{profileUser.followingCount}</div>
                      <div className="text-gray-500">Following</div>
                    </div>
                  </>
                )}
                <div className="text-center">
                  <div className="font-bold text-gray-800 text-lg">{profileUser.reviewsCount}</div>
                  <div className="text-gray-500">Reviews</div>
                </div>
                {isOwnProfile && (
                  <div className="text-center">
                    <div className="font-bold text-gray-800 text-lg">{profileUser.favoritesCount}</div>
                    <div className="text-gray-500">The Vault</div>
                  </div>
                )}
              </div>
            </div>

            {/* Follow button */}
            {!isOwnProfile && (
              <div className="flex-shrink-0">
                <button
                  onClick={handleFollowToggle}
                  disabled={isFollowLoading}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all disabled:opacity-50 ${
                    isFollowing
                      ? 'bg-gray-100 text-gray-700 hover:bg-red-50 hover:text-red-500 border border-gray-200'
                      : 'bg-romcom-red text-white hover:bg-red-600 shadow-md'
                  }`}
                >
                  {isFollowLoading ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : isFollowing ? (
                    <><UserMinus className="w-4 h-4" /> Unfollow</>
                  ) : (
                    <><UserPlus className="w-4 h-4" /> Follow</>
                  )}
                </button>
              </div>
            )}

            {/* Own profile — go to settings */}
            {isOwnProfile && (
              <button
                onClick={() => navigate('/settings')}
                className="flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-200 text-gray-600 rounded-xl text-sm hover:bg-gray-50 transition-colors"
              >
                Edit Profile
              </button>
            )}
          </div>
        </motion.div>

        {/* ── STATS ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
        >
          {[
            ...(isOwnProfile ? [{ icon: Heart, label: 'Favorites', value: profileUser.favoritesCount, color: 'text-romcom-red', bg: 'bg-red-50' }] : []),
            { icon: MessageSquare, label: 'Reviews', value: profileUser.reviewsCount, color: 'text-blue-500', bg: 'bg-blue-50' },
            { icon: Film, label: 'Memory Lane', value: watched.length, color: 'text-purple-500', bg: 'bg-purple-50' },
            { icon: BookOpen, label: 'Notebook', value: notebook.length, color: 'text-pink-500', bg: 'bg-pink-50' },
            ...(isOwnProfile ? [
              { icon: User, label: 'Followers', value: profileUser.followersCount, color: 'text-purple-500', bg: 'bg-purple-50' },
              { icon: User, label: 'Following', value: profileUser.followingCount, color: 'text-green-500', bg: 'bg-green-50' },
            ] : []),
          ].map((stat, i) => (
            <div key={i} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 text-center">
              <div className={`${stat.bg} w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2`}>
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
              </div>
              <div className="text-2xl font-bold text-gray-800">{stat.value}</div>
              <div className="text-gray-500 text-sm">{stat.label}</div>
            </div>
          ))}
        </motion.div>

        {/* ── TABS ── */}
        <div className="flex flex-wrap gap-2 mb-6">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-medium text-sm transition-all ${
                activeTab === tab.key
                  ? 'bg-romcom-red text-white shadow-md'
                  : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── THE VAULT TAB (own profile only) ── */}
        {activeTab === 'favorites' && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            {favorites.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {favorites.map(movie => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
                <Heart className="w-12 h-12 text-gray-200 mx-auto mb-4" />
                <p className="text-gray-500">Add movies you love here</p>
              </div>
            )}
          </motion.div>
        )}

        {/* ── REVIEWS TAB ── */}
        {activeTab === 'reviews' && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            {reviews.length > 0 ? (
              <div className="space-y-4">
                {reviews.map(review => (
                  <div
                    key={review.id}
                    onClick={() => navigate(`/movies/${review.movie.id}`)}
                    className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:border-romcom-pink/30 cursor-pointer transition-all"
                  >
                    <div className="flex gap-4">
                      <div className="w-14 h-20 rounded-lg overflow-hidden flex-shrink-0">
                        {review.movie.posterUrl ? (
                          <img src={review.movie.posterUrl} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-romcom-pink to-romcom-purple flex items-center justify-center">
                            <Film className="w-6 h-6 text-white" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-1">
                          <h4 className="font-bold text-gray-800 truncate">{review.movie.title}</h4>
                          <span className="text-xs text-gray-400 ml-2 flex-shrink-0">
                            {new Date(review.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 mb-2">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`w-3.5 h-3.5 ${i < review.rating ? 'text-yellow-400 fill-current' : 'text-gray-200'}`}
                            />
                          ))}
                          <span className="text-xs text-gray-500 ml-1">{review.rating}/5</span>
                        </div>
                        <p className="text-gray-600 text-sm line-clamp-2">{review.content}</p>
                        <div className="flex gap-3 mt-2 text-xs text-gray-400">
                          <span>❤️ {review.likesCount}</span>
                          <span>💬 {review.commentsCount}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
                <MessageSquare className="w-12 h-12 text-gray-200 mx-auto mb-4" />
                <p className="text-gray-500">No reviews yet!</p>
              </div>
            )}
          </motion.div>
        )}

        {/* ── MEMORY LANE TAB ── */}
        {activeTab === 'memory' && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            {watched.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {watched.map(movie => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
                <Film className="w-12 h-12 text-gray-200 mx-auto mb-4" />
                <p className="text-gray-500">
                  {isOwnProfile ? 'No movies in your Memory Lane yet' : `${profileUser.name || profileUser.username} hasn't added any movies yet`}
                </p>
              </div>
            )}
          </motion.div>
        )}

        {/* ── NOTEBOOK TAB ── */}
        {activeTab === 'notebook' && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            {notebook.length > 0 ? (
              <div className="space-y-4">
                {notebook.map(entry => (
                  <div
                    key={entry.id}
                    onClick={() => navigate(`/movies/${entry.movie.id}`)}
                    className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:border-romcom-pink/30 cursor-pointer transition-all"
                  >
                    <div className="flex gap-4">
                      <div className="w-14 h-20 rounded-lg overflow-hidden flex-shrink-0">
                        {entry.movie.posterUrl ? (
                          <img src={entry.movie.posterUrl} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-romcom-pink to-romcom-purple flex items-center justify-center">
                            <Film className="w-6 h-6 text-white" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-1">
                          <h4 className="font-bold text-gray-800 truncate">{entry.movie.title}</h4>
                          {entry.watchedOn && (
                            <span className="text-xs text-gray-400 ml-2 flex-shrink-0 flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {new Date(entry.watchedOn).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                        {entry.movie.releaseYear && (
                          <p className="text-xs text-gray-400 mb-2">{entry.movie.releaseYear}</p>
                        )}
                        {entry.note ? (
                          <p className="text-gray-600 text-sm line-clamp-3 italic">"{entry.note}"</p>
                        ) : (
                          <p className="text-gray-300 text-sm italic">No note added</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
                <BookOpen className="w-12 h-12 text-gray-200 mx-auto mb-4" />
                <p className="text-gray-500">
                  {isOwnProfile ? 'Your notebook is empty' : `${profileUser.name || profileUser.username} hasn't written any notebook entries yet`}
                </p>
              </div>
            )}
          </motion.div>
        )}

      </div>
    </div>
  );
};

export default UserPage;
